import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { LiveStats } from "@/components/LiveStats";
import { AnimatedLogo } from "@/components/AnimatedLogo";
import { Truck, Gavel, ShieldCheck, MapPin, ArrowRight, Box, Home } from "lucide-react";
import { ContactEmail } from "@/components/ContactEmail";

function HeroArt() {
  return (
    <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="hero-brand-art">
      <div className="relative h-[340px] sm:h-[460px] flex items-center justify-center">
        <div className="relative w-[78%] max-w-[420px] drop-shadow-[0_18px_30px_rgba(93,109,126,0.35)] hover:scale-[1.03] transition-transform duration-500" data-testid="hero-logo-spin">
          <AnimatedLogo className="w-full" />
        </div>
      </div>
      <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-card border border-border rounded-xl p-4 shadow-xl" data-testid="hero-bid-card">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Gavel className="h-5 w-5 text-emerald-600" /></div>
          <div>
            <p className="font-mono text-xs text-muted-foreground uppercase">Lowest bid</p>
            <p className="font-display font-bold text-lg">$420 <span className="text-xs font-normal text-muted-foreground">· 4 offers</span></p>
          </div>
        </div>
      </div>
      <div className="absolute -top-5 -right-2 sm:-right-5 bg-card border border-border rounded-xl p-3 shadow-xl hidden sm:flex items-center gap-3" data-testid="hero-volume-card">
        <div className="h-9 w-9 rounded-lg bg-kinetic-light flex items-center justify-center"><Box className="h-5 w-5 text-kinetic" /></div>
        <div>
          <p className="font-mono text-[10px] text-muted-foreground uppercase">AI video inventory</p>
          <p className="font-display font-bold">18.4 m³ <span className="text-xs font-normal text-muted-foreground">· 3 rooms</span></p>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-28 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BrandLogo size="lg" />
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="outline" onClick={() => navigate("/inventory")} data-testid="nav-inventory-link" className="inline-flex gap-1.5 sm:gap-2 px-2.5 sm:px-4 border-kinetic/40 text-brandgrey hover:text-kinetic hover:border-kinetic sm:mr-1">
            <Box className="h-4 w-4 text-kinetic" /><span className="hidden sm:inline">Create free inventory</span><span className="sm:hidden">Inventory</span>
          </Button>
          <Button variant="ghost" onClick={() => navigate("/auth")} data-testid="landing-login-btn" className="px-2 sm:px-4">Log in</Button>
          <Button onClick={() => navigate("/auth?mode=signup")} data-testid="landing-signup-btn" className="bg-kinetic hover:bg-kinetic-hover text-white gap-2 px-3 sm:px-4 hidden sm:inline-flex">
            Get Started <ArrowRight className="h-4 w-4 hidden sm:block" />
          </Button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-kinetic/30 bg-kinetic-light px-3 py-1 mb-6">
              <span className="h-2 w-2 rounded-full bg-kinetic animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-wider text-kinetic" style={{ fontFamily: "'Montserrat', sans-serif" }}>Australia's Moving Marketplace</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-brandgrey" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="hero-heading">
              Post your move.<br />Let removalists <span className="text-kinetic">tender for it.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed">
              MoveTender is Australia's moving marketplace, the two-sided marketplace where job owners post their move and verified removalists compete with their best price. Transparent bids, better rates, zero hassle.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => navigate("/auth?mode=signup&role=customer")} data-testid="cta-customer-btn" className="bg-kinetic hover:bg-kinetic-hover text-white gap-2 h-12 px-7 text-base font-bold shadow-lg shadow-kinetic/30 hover:-translate-y-0.5 transition-transform">
                <Home className="h-5 w-5" /> Get a Move Quote
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/auth?mode=signup&role=removalist")} data-testid="cta-removalist-btn" className="gap-2 h-12 px-6 border-2">
                <Truck className="h-5 w-5" /> I'm a removalist
              </Button>
            </div>
          </div>
          <HeroArt />
        </div>
      </section>

      <LiveStats />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Gavel, title: "Competitive bidding", desc: "Removalists submit their price and hours. You pick the winning offer — no phone tag." },
            { icon: ShieldCheck, title: "Verified providers", desc: "Every removalist lists their company and ABN/license, verified on the platform." },
            { icon: MapPin, title: "Service zones", desc: "Removalists set the regions they cover, so you only get relevant, ready-to-go bids." },
          ].map((f, i) => (
            <div key={i} className="group rounded-2xl border border-border bg-card p-6 hover:border-kinetic transition-colors animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="h-12 w-12 rounded-xl bg-kinetic-light flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <f.icon className="h-6 w-6 text-kinetic" />
              </div>
              <h3 className="font-display text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid md:grid-cols-2 gap-6" data-testid="landing-path-cards">
        {[
          { to: "/movers", icon: Home, eyebrow: "For customers", title: "Need a removalist?", desc: "See how to post your move, get an AI inventory and compare bids — free for customers.", testid: "path-card-movers", dark: false },
          { to: "/for-removalists", icon: Truck, eyebrow: "For removalists", title: "Win more moving jobs", desc: "Find jobs near you, quote from itemised inventories and bid on the days that suit you.", testid: "path-card-removalists", dark: true },
        ].map((c) => (
          <button key={c.to} type="button" onClick={() => navigate(c.to)} data-testid={c.testid}
            className={`group text-left rounded-2xl p-8 border transition-transform hover:-translate-y-1 ${c.dark ? "bg-asphalt text-white border-asphalt" : "bg-kinetic-light border-kinetic/30"}`}>
            <c.icon className="h-7 w-7 text-kinetic mb-4" />
            <p className={`font-mono text-xs uppercase tracking-widest mb-2 ${c.dark ? "text-white/60" : "text-kinetic"}`}>{c.eyebrow}</p>
            <h2 className="text-2xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{c.title}</h2>
            <p className={`text-sm leading-relaxed mb-5 ${c.dark ? "text-white/70" : "text-muted-foreground"}`}>{c.desc}</p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-kinetic">Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
          </button>
        ))}
      </section>

      <footer className="border-t border-border py-8 px-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground" data-testid="landing-footer">
        <span>MoveTender — Australia's Moving Marketplace</span>
        <button type="button" onClick={() => navigate("/movers")} className="hover:text-kinetic" data-testid="footer-movers-link">Need a removalist?</button>
        <button type="button" onClick={() => navigate("/for-removalists")} className="hover:text-kinetic" data-testid="footer-removalists-link">For removalists</button>
        <ContactEmail />
      </footer>
    </div>
  );
}
